/**
 * Pure mappers: real app data records → `CodingScreenState.values` for the project_starburst shell.
 *
 * This module lives OUTSIDE `project_starburst/` on purpose (the folder's written contract —
 * `interface/state.ts` header + README): it is the out-of-folder adapter seam that maps the app's
 * real data into the folder's source-agnostic `PatientInfoData` / `CaseSpecialtyData` /
 * `CodingScreenState` contract. It is PURE — no redux, no fetch, no React — so Jest, Storybook, and
 * the connected adapter all drive it identically.
 *
 * `CMxAPI.*` and `Accelerate.*` are AMBIENT GLOBALS (declared by `@codametrix/ui-type-definitions`,
 * global-ambient via tsconfig `types`) — referenced with NO import. Only the folder's own type-only
 * `CodingScreenState` / `PatientInfoData` are imported, from the interface barrel.
 *
 * Mode-gated namespaces: coding mode → `case.*` contentIds from the coding `CMxAPI.CaseRun`;
 * grading mode → `workitem.*` contentIds from the grade-bench case record
 * (`state.codeBench.activeItem` — declared `CMxAPI.CaseRun`, a snake_case CQA case at runtime).
 */
import { differenceInYears, parseISO } from 'date-fns';
import type {
  CaseSpecialtyData,
  CodingScreenState,
  PatientInfoData
} from '../../../project_starburst/interface';

/**
 * Age at date of service, derived from date of birth — the coding-domain convention.
 * Shared by the coding and grading mappers, which both carry a
 * birth date + service date. Returns undefined (-> 'N/A') when a date is
 * missing/unparseable or the result is negative. No wall-clock fallback: derivation
 * stays off `new Date()` so the mapper remains pure and deterministic (E2E-safe).
 * Avoids the buggy `patient_age_by_dos_dob` transformer (returns undefined on its
 * truthy branch).
 */
function ageAtService(
  dateOfBirth: string | null | undefined,
  dateOfService: string | null | undefined
): string | undefined {
  if (dateOfBirth == null || dateOfService == null) {
    return undefined;
  }
  const birth = parseISO(dateOfBirth);
  const service = parseISO(dateOfService);
  if (Number.isNaN(birth.getTime()) || Number.isNaN(service.getTime())) {
    return undefined;
  }
  const years = differenceInYears(service, birth);
  return years >= 0 ? String(years) : undefined;
}

/**
 * Map the primary `CMxAPI.CaseRun` (coding mode) to the source-agnostic patient contract.
 *
 * Identifier fields on `CaseRun` are `CaseNumber` (`{ number, issuer, type }`); take `.number` only
 * (`issuer`/`type` are genuinely null in real data). Every field normalizes `null → undefined`
 * because `PatientInfoData` fields are `string | undefined` while `CaseRun` uses `NullableString`.
 */
export function toPatientInfoFromCaseRun(run: CMxAPI.CaseRun): PatientInfoData {
  return {
    fullName: run.patient_name ?? undefined,
    gender: run.patient_gender ?? undefined,
    dob: run.date_of_birth ?? undefined,
    // Prefer the explicit patient_age (null on real cases -> derive age at service).
    age:
      run.patient_age != null
        ? String(run.patient_age)
        : ageAtService(run.date_of_birth, run.date_of_service),
    mrn: run.patient_identifier?.number ?? undefined,
    accountNumber: run.account_number?.number ?? undefined,
    visitNumber: run.visit_number?.number ?? undefined,
    caseId: run.case_id != null ? String(run.case_id) : undefined
  };
}

type CaseRunSpecialty = {
  note_specialty_designation?: string | null;
  facts?: {
    is_hospital_service_same_as_claim_specialty?: string | null;
  } | null;
};

export function toCaseSpecialtyFromCaseRun(
  run: CMxAPI.CaseRun
): CaseSpecialtyData {
  const off = run as unknown as CaseRunSpecialty;
  return {
    specialty: off.note_specialty_designation ?? undefined,
    department: run.scheduling_department ?? undefined,
    billingArea: run.billing_area ?? undefined,
    serviceRole: off.facts?.is_hospital_service_same_as_claim_specialty ?? undefined,
    serviceLine: run.service_line ?? undefined
  };
}

/**
 * The grade-bench's `state.codeBench.activeItem` is DECLARED `CMxAPI.CaseRun`, but at runtime on the
 * CQA grading route (loaded by `loadCQAItemFromURL`) it is a CQA case record: snake_case, with the
 * account number in a flat `hospital_account_record` rather than the `account_number` CaseNumber the
 * coding CaseRun carries. Every OTHER field the grading mapper reads (`patient_name`, `patient_gender`,
 * `date_of_birth`, `date_of_service`, `case_id`) is already on the declared type — only this one is
 * off-type, so this narrow shape names just it, and the source is cast to it through `unknown`.
 */
type CQACaseRecord = {
  hospital_account_record?: string | null;
};

/**
 * Map the grade-bench case record (`state.codeBench.activeItem`) to the source-agnostic patient
 * contract. Age is derived from `date_of_birth` at `date_of_service` (same as the coding side).
 * Account # is the flat `hospital_account_record`. MRN and visit number have no source on this
 * record — those stay grading N/A gaps (`undefined` → 'N/A' at render). Fields normalize
 * `null → undefined`.
 */
export function toPatientInfoFromCodeBenchCase(
  activeItem: CMxAPI.CaseRun
): PatientInfoData {
  return {
    fullName: activeItem.patient_name ?? undefined,
    gender: activeItem.patient_gender ?? undefined,
    dob: activeItem.date_of_birth ?? undefined,
    age: ageAtService(activeItem.date_of_birth, activeItem.date_of_service),
    accountNumber:
      (activeItem as unknown as CQACaseRecord).hospital_account_record ??
      undefined,
    caseId: activeItem.case_id != null ? String(activeItem.case_id) : undefined,
    mrn: undefined,
    visitNumber: undefined
  };
}

/**
 * Build the coding-mode `CodingScreenState` (`case.*` namespace).
 *
 * `case.identifier` is the numeric DB `case_id`, stringified (owner decision). It
 * has no `componentId`, so it MUST be a primitive and is set only when defined: an absent key reads
 * back `undefined` and renders empty, while a non-primitive would throw in `renderPlainText`.
 */
export function codingStateFromCaseRun(run: CMxAPI.CaseRun): CodingScreenState {
  const values: Record<string, unknown> = {
    'case.patient': toPatientInfoFromCaseRun(run),
    'case.specialty': toCaseSpecialtyFromCaseRun(run)
  };
  if (run.case_id != null) {
    values['case.identifier'] = String(run.case_id);
  }
  return { values };
}

/**
 * Build the grading-mode `CodingScreenState` (`workitem.*` namespace) from the codeBench case record.
 * `case_id` is already on the declared `CMxAPI.CaseRun`, so it is read directly (no cast). Same
 * primitive-only / set-when-defined rule for `workitem.identifier` as `case.identifier`.
 */
export function gradingStateFromCodeBenchCase(
  activeItem: CMxAPI.CaseRun
): CodingScreenState {
  const values: Record<string, unknown> = {
    'workitem.patient': toPatientInfoFromCodeBenchCase(activeItem),
    'workitem.specialty': toCaseSpecialtyFromCaseRun(activeItem)
  };
  if (activeItem.case_id != null) {
    values['workitem.identifier'] = String(activeItem.case_id);
  }
  return { values };
}
